"use client";

import React from "react";
import classnames from "classnames";
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Flex,
  Heading,
  Text,
} from "@radix-ui/themes";
import styles from "./PageIndexPage.module.scss";
import searchStyles from "../SearchPage/PageSearchResult.module.scss";
import { PageIndexCategory, PageIndexItem } from "./PageIndexPageClient";
import ClickableLink from "../../components/utilities/ClickableLink";
import FontAwesome from "../../components/utilities/FontAwesome";
import { useT } from "../../components/utilities/TranslatedText";
import CategoryIcon from "../../components/map/CategoryIcon";
import { getCdnUrl } from "../../utils/SchoolBlocksUtilities";
import PageBlockPreview from "./PageBlockPreview";

type PageIndexListProps = {
  items: PageIndexItem[];
  showPublishedBadge?: boolean;
  selectMode?: boolean;
  selected?: Record<string, boolean>;
  onToggleSelected?: (id: string) => void;
};

const MAX_VISIBLE_CATEGORIES = 3;

function CategoryBadge({ category }: { category: PageIndexCategory }) {
  return (
    <Badge size="1" variant="soft" radius="full">
      {category.icon_url && (
        <span className={styles.categoryBadgeIcon} aria-hidden="true">
          <CategoryIcon category={category} />
        </span>
      )}
      {category.category_name}
    </Badge>
  );
}

export default function PageIndexList({
  items,
  showPublishedBadge,
  selectMode,
  selected,
  onToggleSelected,
}: PageIndexListProps) {
  const t = useT();

  if (items.length === 0) {
    return null;
  }

  const elements: React.ReactNode[] = [];
  let previousLetter: string | null = null;

  items.forEach((item, index) => {
    const firstChar = item.title.charAt(0).toUpperCase();
    const firstLetter = /[A-Z]/.test(firstChar) ? firstChar : "#";

    if (firstLetter !== previousLetter) {
      elements.push(
        <Box
          asChild
          key={`header-${firstLetter}`}
          className={styles.letterHeader}
        >
          <li>
            <Heading
              as="h2"
              size="5"
              weight="bold"
              id={`letter-${firstLetter}`}
            >
              {firstLetter}
            </Heading>
          </li>
        </Box>,
      );
      previousLetter = firstLetter;
    }

    const isSelected = selectMode && !!selected?.[item.id];

    elements.push(
      <Flex
        key={item.id || index}
        align="start"
        gap="3"
        py={selectMode ? "1" : "2"}
        px={selectMode ? "2" : undefined}
        mx={selectMode ? "-2" : undefined}
        asChild
      >
        <li
          className={classnames(searchStyles.result, {
            [styles.selectableRow]: selectMode,
            [styles.selectedRow]: isSelected,
          })}
        >
          {selectMode && (
            <Flex
              asChild
              align="center"
              justify="center"
              width="44px"
              height="44px"
              flexShrink="0"
              style={{ cursor: "pointer" }}
            >
              <label>
                <Checkbox
                  size="2"
                  checked={isSelected}
                  onCheckedChange={() => onToggleSelected?.(item.id)}
                  aria-label={`Select ${item.title}`}
                />
              </label>
            </Flex>
          )}
          <Avatar
            size="4"
            radius="small"
            src={item.picture ? getCdnUrl(item.picture) : undefined}
            alt={item.title}
            fallback={
              <FontAwesome prefix="fas" className="fa-file-lines" size={"2x"} />
            }
          />
          <Box flexGrow="1" minWidth="0">
            <ClickableLink href={item.url}>
              <Flex asChild align={"center"}>
                <h2>
                  <PageBlockPreview organizationId={item.id}>
                    {item.title}
                    {showPublishedBadge && item.published !== undefined && (
                      <Badge
                        ml="2"
                        size="2"
                        variant="soft"
                        color={item.published ? "green" : "gray"}
                      >
                        {item.published ? t("Published") : t("Draft")}
                      </Badge>
                    )}
                  </PageBlockPreview>
                </h2>
              </Flex>
              <div className={searchStyles.breadcrumbs}>{item.breadcrumbs}</div>
            </ClickableLink>
            {item.categories && item.categories.length > 0 && (
              <Flex gap="1" mt="1" wrap="wrap" align="center">
                {item.categories.slice(0, MAX_VISIBLE_CATEGORIES).map((cat) => (
                  <CategoryBadge key={cat.id} category={cat} />
                ))}
                {item.categories.length > MAX_VISIBLE_CATEGORIES && (
                  <Text size="1" color="gray" weight="medium">
                    {`+${item.categories.length - MAX_VISIBLE_CATEGORIES} ${t("more")}`}
                  </Text>
                )}
              </Flex>
            )}
          </Box>
        </li>
      </Flex>,
    );
  });

  return (
    <Box asChild className={styles.list}>
      <ul>{elements}</ul>
    </Box>
  );
}
